import React, { useState, useEffect, useMemo } from 'react';
import { apiClient } from '../utils/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { StatusBadge, candidateStatusOptions } from '../components/ui/StatusBadge';
import { recruitmentTheme, themeClasses, getStatusColor } from '../styles/theme';
import { mockCandidates } from '../data/mockData';
import { enhancedSearch } from '../utils/searchUtils';
import { useNotifications } from '../contexts/NotificationContext';
import { CandidateWithMeta } from '../types';
import { migrateStandardCandidates, clearAllFirebaseCandidates } from '../utils/migrateData';
import { testFirebaseConnection, debugFirebaseRules } from '../utils/firebaseDebug';
import { 
  subscribeToCandidates, 
  addCandidate as addCandidateToFirebase,
  updateCandidate as updateCandidateInFirebase,
  softDeleteCandidate
} from '../services/candidateService';
import {
  Users,
  Search,
  Plus,
  Edit,
  Trash2,
  Download,
  Filter,
  X,
  Save,
  Upload,
  User,
  ArrowUpDown,
  GripVertical,
  Settings,
  Clock,
  AlertTriangle,
  FileDown,
  FileSpreadsheet,
  FileText,
  Eye,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LocalStorageManager } from '../utils/localStorage';


type ColumnConfig = {
  [key: string]: {
    title: string;
    width: string;
    sortable: boolean;
    sortField?: keyof CandidateWithMeta;
  };
};

const columnConfig: ColumnConfig = {
  select: { title: '', width: 'w-12', sortable: false },
  name: { title: 'Name', width: '', sortable: true, sortField: 'name' },
  contact: { title: 'Contact', width: '', sortable: false },
  emergencyContact: { title: 'Emergency Contact', width: '', sortable: false },
  position: { title: 'Position', width: '', sortable: true, sortField: 'jobTitle' },
  workCompany: { title: 'Work Company', width: '', sortable: true, sortField: 'workCompany' },
  workCity: { title: 'Work City', width: '', sortable: true, sortField: 'workCity' },
  salary: { title: 'Salary', width: '', sortable: true, sortField: 'salary' },
  stage: { title: 'Stage', width: '', sortable: true, sortField: 'stage' },
  workCountry: { title: 'Work Country', width: '', sortable: true, sortField: 'countryApplyingTo' },
  departure: { title: 'Departure', width: '', sortable: true, sortField: 'departureDate' },
  added: { title: 'Added', width: '', sortable: true, sortField: 'createdAt' },
  actions: { title: 'Actions', width: '', sortable: false },
};

// Export utility functions
const exportToPDF = (candidates: CandidateWithMeta[], selectedIds: string[] = []) => {
  const candidatesToExport = selectedIds.length > 0 
    ? candidates.filter(c => selectedIds.includes(getCandidateId(c)))
    : candidates;

  if (candidatesToExport.length === 0) {
    alert('No candidates to export');
    return;
  }

  // Create a simple HTML content for PDF
  const htmlContent = `
    <html>
      <head>
        <title>Candidates Export</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #8b6914; text-align: center; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; color: #8b6914; }
          .date { font-size: 0.9em; color: #666; }
        </style>
      </head>
      <body>
        <h1>Candidates Export Report</h1>
        <p class="date">Generated on: ${new Date().toLocaleDateString()}</p>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Position</th>
              <th>Emergency Contact</th>
              <th>Work Company</th>
              <th>Work City</th>
              <th>Salary</th>
              <th>Stage</th>
              <th>Work Country</th>
              <th>Departure</th>
              <th>Passport</th>
              <th>Added</th>
            </tr>
          </thead>
          <tbody>
            ${candidatesToExport.map(candidate => `
              <tr>
                <td>${candidate.name}</td>
                <td>${candidate.email}</td>
                <td>${candidate.phone}</td>
                <td>${candidate.jobTitle || 'N/A'}</td>
                <td>${(candidate as any).emergencyContact || 'N/A'}</td>
                <td>${(candidate as any).workCompany || 'N/A'}</td>
                <td>${(candidate as any).workCity || 'N/A'}</td>
                <td>${candidate.salary ? new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(candidate.salary) : 'N/A'}</td>
                <td>${candidate.stage}</td>
                <td>${candidate.countryApplyingTo || 'N/A'}</td>
                <td>${((candidate as any).departureDate && (candidate as any).departureTime) ? `${new Date((candidate as any).departureDate).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })} at ${(candidate as any).departureTime}` : 'Not set'}</td>
                <td>${candidate.passportNumber || 'N/A'}</td>
                <td>${candidate.createdAt ? new Date(candidate.createdAt).toLocaleDateString() : 'N/A'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
    </html>
  `;

  // Create a new window and print
  const newWindow = window.open('', '_blank');
  if (newWindow) {
    newWindow.document.write(htmlContent);
    newWindow.document.close();
    newWindow.print();
  }
};

const exportToCSV = (candidates: CandidateWithMeta[], selectedIds: string[] = []) => {
  const candidatesToExport = selectedIds.length > 0 
    ? candidates.filter(c => selectedIds.includes(getCandidateId(c)))
    : candidates;

  if (candidatesToExport.length === 0) {
    alert('No candidates to export');
    return;
  }

  const headers = ['Name', 'Email', 'Phone', 'Position', 'Emergency Contact', 'Work Company', 'Work City', 'Salary', 'Stage', 'Work Country', 'Departure', 'Passport', 'Added'];
  const csvContent = [
    headers.join(','),
    ...candidatesToExport.map(candidate => [
      `"${candidate.name}"`,
      `"${candidate.email}"`,
      `"${candidate.phone}"`,
      `"${candidate.jobTitle || 'N/A'}"`,
      `"${(candidate as any).emergencyContact || 'N/A'}"`,
      `"${(candidate as any).workCompany || 'N/A'}"`,
      `"${(candidate as any).workCity || 'N/A'}"`,
      `"${candidate.salary || 'N/A'}"`,
      `"${candidate.stage}"`,
      `"${candidate.countryApplyingTo || 'N/A'}"`,
      `"${((candidate as any).departureDate && (candidate as any).departureTime) ? `${new Date((candidate as any).departureDate).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })} at ${(candidate as any).departureTime}` : 'Not set'}"`,
      `"${candidate.passportNumber || 'N/A'}"`,
      `"${candidate.createdAt ? new Date(candidate.createdAt).toLocaleDateString() : 'N/A'}"`
    ].join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `candidates_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

const exportToExcel = (candidates: CandidateWithMeta[], selectedIds: string[] = []) => {
  const candidatesToExport = selectedIds.length > 0 
    ? candidates.filter(c => selectedIds.includes(getCandidateId(c)))
    : candidates;

  if (candidatesToExport.length === 0) {
    alert('No candidates to export');
    return;
  }

  // Create Excel-compatible HTML
  const excelContent = `
    <table>
      <tr>
        <th>Name</th>
        <th>Email</th>
        <th>Phone</th>
        <th>Position</th>
        <th>Emergency Contact</th>
        <th>Work Company</th>
        <th>Work City</th>
        <th>Salary</th>
        <th>Stage</th>
        <th>Work Country</th>
        <th>Departure</th>
        <th>Passport</th>
        <th>Added</th>
      </tr>
      ${candidatesToExport.map(candidate => `
        <tr>
          <td>${candidate.name}</td>
          <td>${candidate.email}</td>
          <td>${candidate.phone}</td>
          <td>${candidate.jobTitle || 'N/A'}</td>
          <td>${(candidate as any).emergencyContact || 'N/A'}</td>
          <td>${(candidate as any).workCompany || 'N/A'}</td>
          <td>${(candidate as any).workCity || 'N/A'}</td>
          <td>${candidate.salary || 'N/A'}</td>
          <td>${candidate.stage}</td>
          <td>${candidate.countryApplyingTo || 'N/A'}</td>
          <td>${((candidate as any).departureDate && (candidate as any).departureTime) ? `${new Date((candidate as any).departureDate).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })} at ${(candidate as any).departureTime}` : 'Not set'}</td>
          <td>${candidate.passportNumber || 'N/A'}</td>
          <td>${candidate.createdAt ? new Date(candidate.createdAt).toLocaleDateString() : 'N/A'}</td>
        </tr>
      `).join('')}
    </table>
  `;

  const blob = new Blob([excelContent], { type: 'application/vnd.ms-excel' });
  const link = document.createElement('a');
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `candidates_export_${new Date().toISOString().split('T')[0]}.xlsx`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

// Helper function to get candidate ID (needs to be moved outside component for export functions)
const getCandidateId = (candidate: CandidateWithMeta): string => {
  return candidate._id || candidate.id || '';
};

const CandidatesPage: React.FC = () => {
  const { notifyBadgeChange, notifyContentChange, showNotification } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const [candidates, setCandidates] = useState<CandidateWithMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>([]);
  const [columnOrder, setColumnOrder] = useState([
    'select', 'name', 'contact', 'emergencyContact', 'position', 'workCompany', 'workCity', 'salary', 'stage', 'workCountry', 'departure', 'added', 'actions'
  ]);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [showAutoDeletionSettings, setShowAutoDeletionSettings] = useState(false);
  const [autoDeletionEnabled, setAutoDeletionEnabled] = useState(false);
  const [autoDeletionDays, setAutoDeletionDays] = useState(30);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<CandidateWithMeta | null>(null);
  const [editingDeparture, setEditingDeparture] = useState<string | null>(null);
  const [departureEditData, setDepartureEditData] = useState({ date: '', time: '' });
  const [expandedCandidates, setExpandedCandidates] = useState<Set<string>>(new Set());
  const [formData, setFormData] = useState({
    // Basic info - matches CV Builder
    name: '', // matches fullName in CV Builder
    email: '',
    phone: '',
    jobTitle: '', // matches position in CV Builder
    salary: '',
    stage: 'Onboarding',
    countryApplyingTo: '', // matches workCountry in CV Builder
    contact: '', // matches contact in CV Builder
    emergencyContact: '', // matches emergencyContact in CV Builder
    workCompany: '', // matches workCompany in CV Builder
    workCity: '', // matches workCity in CV Builder
    city: '',
    country: '', // current residence
    passportNumber: '', // matches passportNo in CV Builder
    notes: '', // matches remarks in CV Builder
    cvFile: null as File | null,
    
    // Additional CV Builder fields
    nationality: '',
    religion: '',
    dateOfBirth: '',
    placeOfBirth: '',
    father: '',
    mother: '',
    age: '',
    height: '',
    weight: '',
    civilStatus: '',
    spouse: '',
    numberOfKids: '',
    educationLevel: '',
    englishLevel: '',
    arabicLevel: '',
    workPosition: '', // previous work experience
    workYears: '',
    
    // Passport info
    dateIssued: '',
    dateExpiry: '',
    placeIssued: ''
  });

  const stages = ['Onboarding', 'Interviewing', 'Offer', 'Hired', 'Rejected'];
  const countries = [
    'Saudi Arabia',
    'UAE',
    'Kuwait',
    'Qatar',
    'Bahrain',
    'Oman',
    'Jordan',
    'Lebanon',
    'Other'
  ];


  // Subscribe to real-time candidates updates from Firebase
  useEffect(() => {
    console.log('📡 Setting up Firebase real-time listener for candidates...');
    console.log('🔍 Current candidates state before Firebase listener:', candidates.length);
    
    let unsubscribe: (() => void) | null = null;
    
    const setupSubscription = async () => {
      try {
        setLoading(true);
        unsubscribe = await subscribeToCandidates((firebaseCandidates) => {
          console.log('🔄 Received', firebaseCandidates.length, 'candidates from Firebase');
          console.log('📋 Firebase candidates data:', firebaseCandidates);
          setCandidates(firebaseCandidates);
          setLoading(false);
        }, (error) => {
          console.error('❌ Firebase candidates subscription error:', error);
          console.error('❌ Error details:', error.message, error.code);
          // Show user-friendly error message
          alert(`Firebase connection error: ${error.message}. Candidates may not persist.`);
          setLoading(false);
        });
      } catch (error) {
        console.error('❌ Failed to setup Firebase candidates subscription:', error);
        alert('Failed to connect to Firebase. Candidates will not persist.');
        setLoading(false);
      }
    };
    
    setupSubscription();
    
    // Cleanup subscription on unmount
    return () => {
      console.log('🔌 Cleaning up Firebase candidates subscription...');
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);


  // Handle navigation state for opening add modal
  useEffect(() => {
    const state = location.state as { openAddModal?: boolean } | null;
    if (state?.openAddModal) {
      setShowAddModal(true);
      // Clear the state to prevent reopening on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  // Memoize filtered and sorted candidates for performance
  const filteredCandidates = useMemo(() => {
    let processedCandidates = [...candidates];

    // Apply search filter
    if (searchTerm) {
      processedCandidates = processedCandidates.filter(candidate => {
        const searchableFields = [
          candidate.name,
          candidate.email,
          candidate.phone,
          (candidate as any).contact || '',
          (candidate as any).emergencyContact || '',
          candidate.jobTitle || '',
          (candidate as any).workCompany || '',
          (candidate as any).workCity || '',
          candidate.city || '',
          candidate.countryApplyingTo || '',
          candidate.passportNumber || '',
          candidate.stage,
          candidate.salary?.toString() || ''
        ];
        return enhancedSearch(searchTerm, searchableFields);
      });
    }

    // Apply stage filter
    if (stageFilter !== 'all') {
      processedCandidates = processedCandidates.filter(candidate => candidate.stage === stageFilter);
    }

    setTotalPages(Math.ceil(processedCandidates.length / 10));
    return processedCandidates;

  }, [candidates, searchTerm, stageFilter]);


  const getStageColor = (stage: string) => {
    return getStatusColor(stage);
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDepartureDateTime = (date?: string, time?: string) => {
    if (!date) return 'Set departure';
    
    const dateObj = new Date(date);
    const formattedDate = dateObj.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).toUpperCase();
    
    if (time) {
      return `${formattedDate} at ${time}`;
    }
    return formattedDate;
  };

  const handleSaveDeparture = async (candidateId: string) => {
    try {
      const candidate = candidates.find(c => getCandidateId(c) === candidateId);
      if (!candidate) return;

      const updates = {
        departureDate: departureEditData.date,
        departureTime: departureEditData.time
      };

      // Update using Firebase
      await updateCandidateInFirebase(candidateId, updates);
      
      setEditingDeparture(null);
      setDepartureEditData({ date: '', time: '' });
      
      showNotification({
        type: 'success',
        title: 'Departure Updated',
        message: `${candidate.name}'s departure has been set for ${formatDepartureDateTime(departureEditData.date, departureEditData.time)}`,
        duration: 5000
      });
    } catch (error) {
      console.error('Failed to save departure:', error);
      showNotification({
        type: 'error',
        title: 'Update Failed',
        message: 'Failed to save departure date/time. Please try again.',
        duration: 5000
      });
    }
  };

  const resetForm = () => {
    setFormData({
      // Basic info - matches CV Builder
      name: '',
      email: '',
      phone: '',
      jobTitle: '',
      salary: '',
      stage: 'Onboarding',
      countryApplyingTo: '',
      contact: '',
      emergencyContact: '',
      workCompany: '',
      workCity: '',
      city: '',
      country: '',
      passportNumber: '',
      notes: '',
      cvFile: null,
      
      // Additional CV Builder fields
      nationality: '',
      religion: '',
      dateOfBirth: '',
      placeOfBirth: '',
      father: '',
      mother: '',
      age: '',
      height: '',
      weight: '',
      civilStatus: '',
      spouse: '',
      numberOfKids: '',
      educationLevel: '',
      englishLevel: '',
      arabicLevel: '',
      workPosition: '',
      workYears: '',
      
      // Passport info
      dateIssued: '',
      dateExpiry: '',
      placeIssued: ''
    });
  };

  const updateCandidateStage = async (candidateId: string, newStage: string) => {
    try {
      const candidate = candidates.find(c => getCandidateId(c) === candidateId);
      if (!candidate) return;
      
      const oldStage = candidate.stage;
      
      // Update using Firebase
      await updateCandidateInFirebase(candidateId, { stage: newStage as any });
      
      // Notify badge change
      notifyBadgeChange('Candidates Page', `${candidate.name}'s Stage`, oldStage, newStage);
      
      showNotification({
        type: 'success',
        title: 'Stage Updated',
        message: `${candidate.name}'s stage changed from ${oldStage} to ${newStage}`,
        duration: 5000
      });
    } catch (error) {
      console.error('Failed to update candidate stage:', error);
      showNotification({
        type: 'error',
        title: 'Update Failed',
        message: 'Failed to update candidate stage. Please try again.',
        duration: 5000
      });
    }
  };

  const handleSelectCandidate = (candidateId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedCandidates(prev => [...prev, candidateId]);
    } else {
      setSelectedCandidates(prev => prev.filter(id => id !== candidateId));
    }
  };

  const handleSelectAll = (isSelected: boolean) => {
    const paginatedIds = paginatedCandidates.map(candidate => getCandidateId(candidate));
    if (isSelected) {
      setSelectedCandidates(prev => [...new Set([...prev, ...paginatedIds])]);
    } else {
      setSelectedCandidates(prev => prev.filter(id => !paginatedIds.includes(id)));
    }
  };

  const toggleCandidateExpansion = (candidateId: string) => {
    setExpandedCandidates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(candidateId)) {
        newSet.delete(candidateId);
      } else {
        newSet.add(candidateId);
      }
      return newSet;
    });
  };

  const handleDeleteSelected = async () => {
    if (window.confirm(`Are you sure you want to delete ${selectedCandidates.length} candidate(s)? They will be moved to the recycle bin.`)) {
      try {
        let deletedCount = 0;
        
        // Delete each selected candidate using Firebase soft delete
        const deletePromises = selectedCandidates.map(async (candidateId) => {
          try {
            await softDeleteCandidate(candidateId);
            deletedCount++;
            console.log(`✅ Successfully soft deleted candidate ${candidateId} from Firebase`);
          } catch (error) {
            console.error(`❌ Error soft deleting candidate ${candidateId} from Firebase:`, error);
          }
        });
        
        // Wait for all deletions to complete
        await Promise.all(deletePromises);
        
        setSelectedCandidates([]);
        alert(`${deletedCount} candidate(s) moved to recycle bin!`);
      } catch (error) {
        console.error('Failed to delete candidates:', error);
        alert('Failed to delete some candidates. Please try again.');
      }
    }
  };

  const handleBulkStageUpdate = async (newStage: string) => {
    try {
      let updatedCount = 0;
      
      // Update each selected candidate using Firebase
      const updatePromises = selectedCandidates.map(async (candidateId) => {
        try {
          await updateCandidateInFirebase(candidateId, { stage: newStage as any });
          updatedCount++;
          console.log(`✅ Successfully updated candidate ${candidateId} stage to ${newStage}`);
        } catch (error) {
          console.error('❌ Failed to update candidate stage:', candidateId, error);
        }
      });
      
      // Wait for all updates to complete
      await Promise.all(updatePromises);
      
      setSelectedCandidates([]);
      showNotification({
        type: 'success',
        title: 'Stages Updated',
        message: `${updatedCount} candidate(s) stage updated to ${newStage}`,
        duration: 5000
      });
    } catch (error) {
      console.error('Failed to update candidate stages:', error);
      alert('Failed to update some candidate stages. Please try again.');
    }
  };

  const handleAutoDeletionToggle = () => {
    setAutoDeletionEnabled(!autoDeletionEnabled);
    if (!autoDeletionEnabled) {
      alert(`Auto-deletion enabled: Completed candidates will be deleted after ${autoDeletionDays} days`);
    } else {
      alert('Auto-deletion disabled');
    }
  };

  const getCompletedCandidatesForDeletion = () => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - autoDeletionDays);
    
    return candidates.filter(candidate => 
      candidate.stage === 'Hired' && 
      candidate.createdAt &&
      new Date(candidate.createdAt) < cutoffDate
    );
  };

  const executeAutoDeletion = () => {
    const candidatesToDelete = getCompletedCandidatesForDeletion();
    
    if (candidatesToDelete.length === 0) {
      alert('No candidates eligible for auto-deletion at this time.');
      return;
    }

    if (window.confirm(
      `Auto-delete ${candidatesToDelete.length} completed candidate(s) that are older than ${autoDeletionDays} days?\n\n` +
      candidatesToDelete.map(c => `• ${c.name} (${c.stage})`).join('\n')
    )) {
      setCandidates(prev => 
        prev.filter(candidate => !candidatesToDelete.some(del => getCandidateId(del) === getCandidateId(candidate)))
      );
      alert(`${candidatesToDelete.length} completed candidate(s) auto-deleted successfully!`);
    }
  };

  // Auto-deletion effect
  React.useEffect(() => {
    if (autoDeletionEnabled) {
      const interval = setInterval(() => {
        const candidatesToDelete = getCompletedCandidatesForDeletion();
        if (candidatesToDelete.length > 0) {
          console.log(`Auto-deletion: Found ${candidatesToDelete.length} candidates ready for deletion`);
          // In production, this would be handled by a background service
        }
      }, 24 * 60 * 60 * 1000); // Check daily
      
      return () => clearInterval(interval);
    }
  }, [autoDeletionEnabled, autoDeletionDays, candidates]);

  const handleDragStart = (e: React.DragEvent, columnId: string) => {
    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === targetColumnId) return;

    const newOrder = [...columnOrder];
    const draggedIndex = newOrder.indexOf(draggedColumn);
    const targetIndex = newOrder.indexOf(targetColumnId);

    // Remove dragged column from its current position
    newOrder.splice(draggedIndex, 1);
    // Insert it at the target position
    newOrder.splice(targetIndex, 0, draggedColumn);

    setColumnOrder(newOrder);
    setDraggedColumn(null);
  };

  const renderCell = (candidate: CandidateWithMeta, columnId: string) => {
    switch (columnId) {
      case 'select':
        return (
          <input
            type="checkbox"
            checked={selectedCandidates.includes(getCandidateId(candidate))}
            onChange={(e) => handleSelectCandidate(getCandidateId(candidate), e.target.checked)}
            className="rounded"
          />
        );
      case 'name':
        return (
          <div>
            <div className="font-medium text-[#8b6914]">{candidate.name}</div>
            <div className="text-sm text-gray-500">{candidate.passportNumber || 'No passport'}</div>
          </div>
        );
      case 'contact':
        return (
          <div>
            <a 
              href={`mailto:${candidate.email}`}
              className="text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors"
              title="Send email"
            >
              {candidate.email}
            </a>
            <br />
            <a 
              href={`tel:${candidate.phone}`}
              className="text-sm text-green-600 hover:text-green-800 hover:underline transition-colors"
              title="Call this number"
            >
              {candidate.phone}
            </a>
          </div>
        );
      case 'position':
        return <span className="text-sm text-gray-900">{candidate.jobTitle || 'N/A'}</span>;
      case 'emergencyContact':
        return <span className="text-sm text-gray-900">{(candidate as any).emergencyContact || 'N/A'}</span>;
      case 'workCompany':
        return <span className="text-sm text-gray-900">{(candidate as any).workCompany || 'N/A'}</span>;
      case 'workCity':
        return <span className="text-sm text-gray-900">{(candidate as any).workCity || 'N/A'}</span>;
      case 'salary':
        return <span className="text-sm text-gray-900">{formatCurrency(candidate.salary)}</span>;
      case 'stage':
        return (
          <StatusBadge
            currentStatus={candidate.stage}
            options={candidateStatusOptions}
            onStatusChange={(newStatus) => {
              const candidateId = getCandidateId(candidate);
              if (candidateId) {
                updateCandidateStage(candidateId, newStatus);
              }
            }}
            size="sm"
          />
        );
      case 'workCountry':
        return <span className="text-sm text-gray-900">{candidate.countryApplyingTo || 'N/A'}</span>;
      case 'departure':
        const candidateId = getCandidateId(candidate);
        const isEditing = editingDeparture === candidateId;
        const departureDate = (candidate as any).departureDate;
        const departureTime = (candidate as any).departureTime;
        
        if (isEditing) {
          return (
            <div className="flex flex-col space-y-2 min-w-[200px]">
              <div className="flex space-x-2">
                <input
                  type="date"
                  value={departureEditData.date}
                  onChange={(e) => setDepartureEditData(prev => ({ ...prev, date: e.target.value }))}
                  className="text-xs border border-gray-300 rounded px-2 py-1 flex-1"
                />
                <input
                  type="time"
                  value={departureEditData.time}
                  onChange={(e) => setDepartureEditData(prev => ({ ...prev, time: e.target.value }))}
                  className="text-xs border border-gray-300 rounded px-2 py-1 flex-1"
                />
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={() => handleSaveDeparture(candidateId)}
                  className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 transition-colors"
                  disabled={!departureEditData.date || !departureEditData.time}
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setEditingDeparture(null);
                    setDepartureEditData({ date: '', time: '' });
                  }}
                  className="text-xs bg-gray-500 text-white px-2 py-1 rounded hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          );
        }
        
        return (
          <div 
            onClick={() => {
              setEditingDeparture(candidateId);
              setDepartureEditData({
                date: departureDate || '',
                time: departureTime || ''
              });
            }}
            className="cursor-pointer hover:text-[#8b6914] transition-colors flex items-center space-x-1"
            title="Click to set departure date/time"
          >
            <Clock className="w-4 h-4 text-gray-400" />
            <span className="text-sm">
              {formatDepartureDateTime(departureDate, departureTime)}
            </span>
          </div>
        );
      case 'added':
        return <span className="text-sm text-gray-500">{formatDate(candidate.createdAt)}</span>;
      case 'actions':
        return (
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => handleViewCV(candidate)}
              className={`${recruitmentTheme.components.button.ghost} text-green-600 hover:text-green-700`}
              title="View CV"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button 
              onClick={() => navigate('/cv-builder', { 
                state: { 
                  candidate: {
                    id: getCandidateId(candidate),
                    _id: getCandidateId(candidate),
                    name: candidate.name,
                    fullName: candidate.name,
                    email: candidate.email,
                    emailAddress: candidate.email,
                    phone: candidate.phone,
                    contactNumber: candidate.phone,
                    passportNumber: candidate.passportNumber,
                    city: candidate.city,
                    country: candidate.country,
                    countryApplyingTo: candidate.countryApplyingTo,
                    contact: (candidate as any).contact,
                    emergencyContact: (candidate as any).emergencyContact,
                    workCompany: (candidate as any).workCompany,
                    workCity: (candidate as any).workCity,
                    jobTitle: candidate.jobTitle,
                    salary: candidate.salary,
                    stage: candidate.stage,
                    notes: (candidate as any).notes || (candidate as any).remarks,
                    // Add more comprehensive mapping
                    address: (candidate as any).address,
                    nationality: (candidate as any).nationality,
                    dateOfBirth: (candidate as any).dateOfBirth,
                    placeOfBirth: (candidate as any).placeOfBirth,
                    civilStatus: (candidate as any).civilStatus || (candidate as any).maritalStatus,
                    religion: (candidate as any).religion,
                    height: (candidate as any).height,
                    weight: (candidate as any).weight,
                    father: (candidate as any).father || (candidate as any).fatherName,
                    mother: (candidate as any).mother || (candidate as any).motherName,
                    spouse: (candidate as any).spouse,
                    numberOfKids: (candidate as any).numberOfKids || (candidate as any).children,
                    educationLevel: (candidate as any).educationLevel,
                    workPosition: (candidate as any).workPosition,
                    workYears: (candidate as any).workYears,
                    workCountry: (candidate as any).workCountry,
                    englishLevel: (candidate as any).englishLevel,
                    arabicLevel: (candidate as any).arabicLevel,
                    allSkills: (candidate as any).allSkills || [],
                    age: (candidate as any).age,
                    gender: (candidate as any).gender,
                    dateIssued: (candidate as any).dateIssued,
                    dateExpiry: (candidate as any).dateExpiry,
                    placeIssued: (candidate as any).placeIssued,
                    idNumber: (candidate as any).idNumber,
                    medicalHistory: (candidate as any).medicalHistory,
                    hobbies: (candidate as any).hobbies,
                    complexion: (candidate as any).complexion
                  }
                }
              })}
              className={`${recruitmentTheme.components.button.ghost} text-blue-600 hover:text-blue-700`}
              title="Create/Edit CV"
            >
              <User className="w-4 h-4" />
            </button>
            <button 
              onClick={() => openEditModal(candidate)}
              className={recruitmentTheme.components.button.ghost}
              title="Edit candidate"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button 
              onClick={() => handleDownloadCV(candidate)}
              className={recruitmentTheme.components.button.ghost}
              title="Download CV"
            >
              <Download className="w-4 h-4" />
            </button>
            <button 
              onClick={() => handleDeleteCandidate(getCandidateId(candidate), candidate.name)}
              className={`${recruitmentTheme.components.button.ghost} text-red-600 hover:text-red-700`}
              title="Delete candidate"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('File size must be less than 5MB');
        return;
      }
      setFormData({ ...formData, cvFile: file });
    }
  };

  const handleAddCandidate = async () => {
    try {
      const newCandidateData = {
        name: formData.name,
        email: formData.email || `${formData.name.toLowerCase().replace(/\s+/g, '.')}` + '@email.com',
        phone: formData.phone || '+254700000000',
        jobTitle: formData.jobTitle,
        salary: formData.salary ? parseInt(formData.salary) : undefined,
        stage: formData.stage as any,
        countryApplyingTo: formData.countryApplyingTo,
        contact: formData.contact,
        emergencyContact: formData.emergencyContact,
        workCompany: formData.workCompany,
        workCity: formData.workCity,
        passportNumber: formData.passportNumber,
        city: formData.city,
        country: formData.country,
        notes: formData.notes,
      };

      // Add candidate using Firebase
      await addCandidateToFirebase(newCandidateData);

      setShowAddModal(false);
      resetForm();
      
      // Notify content change
      notifyContentChange('Candidates Page', 'added', 'Candidate', formData.name);
      
      showNotification({
        type: 'success',
        title: 'Candidate Added',
        message: `${formData.name} has been added successfully!`,
        duration: 5000
      });
    } catch (error: any) {
      console.error('Failed to add candidate:', error);
      
      const errorMessage = error.message?.includes('already exists') 
        ? 'A candidate with the same details already exists and has been updated instead.'
        : 'Failed to add candidate. Please try again.';
      
      showNotification({
        type: 'error',
        title: 'Add Failed',
        message: errorMessage,
        duration: 5000
      });
    }
  };

  const handleEditCandidate = async () => {
    if (!selectedCandidate) return;
    
    try {
      const candidateId = selectedCandidate._id || selectedCandidate.id;
      if (!candidateId) {
        alert('Invalid candidate ID');
        return;
      }
      
      const updates = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        jobTitle: formData.jobTitle,
        salary: formData.salary ? parseInt(formData.salary) : undefined,
        stage: formData.stage as any,
        countryApplyingTo: formData.countryApplyingTo,
        passportNumber: formData.passportNumber
      };
      
      // Update using Firebase
      await updateCandidateInFirebase(candidateId, updates);
      
      setShowEditModal(false);
      setSelectedCandidate(null);
      resetForm();
      
      showNotification({
        type: 'success',
        title: 'Candidate Updated',
        message: `${formData.name} has been updated successfully!`,
        duration: 5000
      });
    } catch (error: any) {
      console.error('Failed to update candidate:', error);
      showNotification({
        type: 'error',
        title: 'Update Failed',
        message: 'Failed to update candidate. Please try again.',
        duration: 5000
      });
    }
  };

  const handleDeleteCandidate = async (candidateId: string, candidateName: string) => {
    if (!window.confirm(`Are you sure you want to delete ${candidateName}? They will be moved to the recycle bin.`)) {
      return;
    }
    
    try {
      // Use Firebase soft delete
      await softDeleteCandidate(candidateId);
      console.log(`✅ Successfully soft deleted candidate ${candidateId} from Firebase`);
      
      showNotification({
        type: 'success',
        title: 'Candidate Deleted',
        message: `${candidateName} has been moved to the recycle bin.`,
        duration: 5000
      });
    } catch (error: any) {
      console.error('Failed to delete candidate:', error);
      showNotification({
        type: 'error',
        title: 'Delete Failed',
        message: 'Failed to delete candidate. Please try again.',
        duration: 5000
      });
    }
  };

  const openEditModal = (candidate: CandidateWithMeta) => {
    setSelectedCandidate(candidate);
    setFormData({
      name: candidate.name,
      email: candidate.email,
      phone: candidate.phone,
      jobTitle: candidate.jobTitle || '',
      salary: candidate.salary?.toString() || '',
      stage: candidate.stage,
      countryApplyingTo: candidate.countryApplyingTo || '',
      contact: (candidate as any).contact || '',
      emergencyContact: (candidate as any).emergencyContact || '',
      workCompany: (candidate as any).workCompany || '',
      workCity: (candidate as any).workCity || '',
      city: candidate.city || '',
      country: candidate.country || '',
      passportNumber: candidate.passportNumber || '',
      notes: '',
      cvFile: null,
      
      // Additional CV Builder fields
      nationality: '',
      religion: '',
      dateOfBirth: '',
      placeOfBirth: '',
      father: '',
      mother: '',
      age: '',
      height: '',
      weight: '',
      civilStatus: '',
      spouse: '',
      numberOfKids: '',
      educationLevel: '',
      englishLevel: '',
      arabicLevel: '',
      workPosition: '',
      workYears: '',
      
      // Passport info
      dateIssued: '',
      dateExpiry: '',
      placeIssued: ''
    });
    setShowEditModal(true);
  };

  const handleDownloadCV = (candidate: CandidateWithMeta) => {
    alert(`Download CV for ${candidate.name} - Feature coming soon!`);
  };

  const handleViewCV = (candidate: CandidateWithMeta) => {
    const candidateId = getCandidateId(candidate);
    
    showNotification({
      type: 'info',
      title: 'Loading CV',
      message: `Opening CV Builder for ${candidate.name}...`,
      duration: 3000
    });
    
    // Always navigate to CV Builder with the candidate data
    // The CV Builder will handle loading any existing CVs or drafts
    navigate('/cv-builder', { 
      state: { 
        candidate: {
          id: candidateId,
          _id: candidateId,
          name: candidate.name,
          fullName: candidate.name,
          email: candidate.email,
          emailAddress: candidate.email,
          phone: candidate.phone,
          contactNumber: candidate.phone,
          passportNumber: candidate.passportNumber,
          city: candidate.city,
          country: candidate.country,
          countryApplyingTo: candidate.countryApplyingTo,
          contact: (candidate as any).contact,
          emergencyContact: (candidate as any).emergencyContact,
          workCompany: (candidate as any).workCompany,
          workCity: (candidate as any).workCity,
          jobTitle: candidate.jobTitle,
          salary: candidate.salary,
          stage: candidate.stage,
          notes: (candidate as any).notes || (candidate as any).remarks,
          // Add more comprehensive mapping
          address: (candidate as any).address,
          nationality: (candidate as any).nationality,
          dateOfBirth: (candidate as any).dateOfBirth,
          placeOfBirth: (candidate as any).placeOfBirth,
          civilStatus: (candidate as any).civilStatus || (candidate as any).maritalStatus,
          religion: (candidate as any).religion,
          height: (candidate as any).height,
          weight: (candidate as any).weight,
          father: (candidate as any).father || (candidate as any).fatherName,
          mother: (candidate as any).mother || (candidate as any).motherName,
          spouse: (candidate as any).spouse,
          numberOfKids: (candidate as any).numberOfKids || (candidate as any).children,
          educationLevel: (candidate as any).educationLevel,
          workPosition: (candidate as any).workPosition,
          workYears: (candidate as any).workYears,
          workCountry: (candidate as any).workCountry,
          englishLevel: (candidate as any).englishLevel,
          arabicLevel: (candidate as any).arabicLevel,
          allSkills: (candidate as any).allSkills || [],
          age: (candidate as any).age,
          gender: (candidate as any).gender,
          dateIssued: (candidate as any).dateIssued,
          dateExpiry: (candidate as any).dateExpiry,
          placeIssued: (candidate as any).placeIssued,
          idNumber: (candidate as any).idNumber,
          medicalHistory: (candidate as any).medicalHistory,
          hobbies: (candidate as any).hobbies,
          complexion: (candidate as any).complexion
        }
      }
    });
  };

  const handleCreateCV = () => {
    // Map candidate form data to CV Builder format
    const cvBuilderData = {
      // Basic mapping
      fullName: formData.name,
      passportNo: formData.passportNumber,
      position: formData.jobTitle,
      salary: formData.salary ? `${formData.salary} KES` : '',
      address: formData.city && formData.country ? `${formData.city}, ${formData.country}` : '',
      workCountry: formData.countryApplyingTo,
      remarks: formData.notes,
      
      // Direct mappings
      nationality: formData.nationality,
      religion: formData.religion,
      dateOfBirth: formData.dateOfBirth,
      placeOfBirth: formData.placeOfBirth,
      father: formData.father,
      mother: formData.mother,
      age: formData.age,
      height: formData.height,
      weight: formData.weight,
      civilStatus: formData.civilStatus,
      spouse: formData.spouse,
      numberOfKids: formData.numberOfKids,
      educationLevel: formData.educationLevel,
      englishLevel: formData.englishLevel,
      arabicLevel: formData.arabicLevel,
      workPosition: formData.workPosition,
      workYears: formData.workYears,
      
      // Passport details
      dateIssued: formData.dateIssued,
      dateExpiry: formData.dateExpiry,
      placeIssued: formData.placeIssued,
      
      // Additional fields from candidate
      email: formData.email,
      phone: formData.phone,
      stage: formData.stage
    };
    
    // Store mapped data in localStorage for CV Builder
    localStorage.setItem('candidateFormData', JSON.stringify(cvBuilderData));
    navigate('/cv-builder');
  };

  const handleSort = (field: string) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
  };

  // Sort candidates based on current sort settings
  const sortedCandidates = useMemo(() => {
    if (!sortField) return filteredCandidates;

    return [...filteredCandidates].sort((a, b) => {
      const aValue = (a as any)[sortField];
      const bValue = (b as any)[sortField];

      // Handle null or undefined values to avoid errors, placing them at the end.
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      const comparableA = typeof aValue === 'string' ? aValue.toLowerCase() : aValue;
      const comparableB = typeof bValue === 'string' ? bValue.toLowerCase() : bValue;

      let comparison = 0;
      if (comparableA > comparableB) {
        comparison = 1;
      } else if (comparableA < comparableB) {
        comparison = -1;
      }

      return sortDirection === 'desc' ? -comparison : comparison;
    });
  }, [filteredCandidates, sortField, sortDirection]);

  const paginatedCandidates = useMemo(() => {
    const startIndex = (currentPage - 1) * 10;
    return sortedCandidates.slice(startIndex, startIndex + 10);
  }, [sortedCandidates, currentPage]);

  const areAllOnPageSelected = useMemo(() => {
    if (paginatedCandidates.length === 0) return false;
    return paginatedCandidates.every(c => selectedCandidates.includes(getCandidateId(c)));
  }, [paginatedCandidates, selectedCandidates]);

  return (
    <div className="space-y-4 lg:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className={`${themeClasses.pageTitle} ${recruitmentTheme.animations.fadeIn}`}>Candidates</h1>
          <p className="text-gray-600 mt-1">
            Manage and track all candidates in your recruitment pipeline
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:space-x-3">
          <button 
            onClick={migrateStandardCandidates}
            className={`${recruitmentTheme.components.button.primary} flex items-center space-x-2 text-sm`}
            title="Add standard candidates to Firebase for consistent data across devices"
          >
            <Users className="w-4 h-4" />
            <span>Sync Standard Data</span>
          </button>
          <button 
            onClick={clearAllFirebaseCandidates}
            className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-2 text-sm text-red-600 hover:text-red-700`}
            title="Clear all Firebase data (WARNING: Permanent deletion)"
          >
            <Trash2 className="w-4 h-4" />
            <span>Clear Firebase</span>
          </button>
          <div className="flex items-center space-x-1 sm:space-x-2 border-r border-gray-300 pr-2 sm:pr-3">
            <span className="text-xs sm:text-sm text-gray-600 hidden sm:inline">Export All:</span>
            <button 
              onClick={() => exportToPDF(candidates)}
              className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-1 text-sm`}
              title="Export all candidates to PDF"
            >
              <FileText className="w-4 h-4" />
              <span>PDF</span>
            </button>
            <button 
              onClick={() => exportToExcel(candidates)}
              className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-1 text-sm`}
              title="Export all candidates to Excel"
            >
              <FileSpreadsheet className="w-4 h-4" />
              <span>Excel</span>
            </button>
            <button 
              onClick={() => exportToCSV(candidates)}
              className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-1 text-sm`}
              title="Export all candidates to CSV"
            >
              <FileDown className="w-4 h-4" />
              <span>CSV</span>
            </button>
          </div>
          <button 
            onClick={() => setShowAutoDeletionSettings(true)}
            className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-2`}
            title="Auto-deletion settings"
          >
            <Settings className="w-4 h-4" />
            <span>Auto-Delete</span>
            {autoDeletionEnabled && (
              <div className="w-2 h-2 bg-green-500 rounded-full" title="Auto-deletion enabled" />
            )}
          </button>
        </div>
      </div>

      <div className={recruitmentTheme.components.card}>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex items-center space-x-2">
              <Users className="w-5 h-5 text-[#8b6914]" />
              <CardTitle className="text-[#8b6914]">All Candidates</CardTitle>
              <button 
                onClick={() => navigate('/cv-builder')}
                className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-2 ml-4`}
                data-add="true"
              >
                <Plus className="w-4 h-4" />
                <span>Add Candidate</span>
              </button>
              <div className="relative ml-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search names, emails, countries, passport numbers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`${recruitmentTheme.components.input} pl-10 w-80`}
                  data-search="local"
                />
              </div>
              <select
                value={stageFilter}
                onChange={(e) => setStageFilter(e.target.value)}
                className={recruitmentTheme.components.select}
              >
                <option value="all">All Stages</option>
                {stages.map(stage => (
                  <option key={stage} value={stage}>{stage}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Bulk Actions */}
          {selectedCandidates.length > 0 && (
            <div className="flex items-center justify-between mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <span className="text-sm text-blue-700">
                {selectedCandidates.length} candidate(s) selected
              </span>
              <div className="flex items-center space-x-2">
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      handleBulkStageUpdate(e.target.value);
                      e.target.value = '';
                    }
                  }}
                  className="text-sm border border-blue-300 rounded px-2 py-1 bg-white"
                  defaultValue=""
                >
                  <option value="">Update Stage</option>
                  {stages.map(stage => (
                    <option key={stage} value={stage}>{stage}</option>
                  ))}
                </select>
                <button
                  onClick={() => exportToPDF(candidates, selectedCandidates)}
                  className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-1 text-sm`}
                  title="Export selected to PDF"
                >
                  <FileText className="w-4 h-4" />
                  <span>PDF</span>
                </button>
                <button
                  onClick={() => exportToExcel(candidates, selectedCandidates)}
                  className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-1 text-sm`}
                  title="Export selected to Excel"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  <span>Excel</span>
                </button>
                <button
                  onClick={() => exportToCSV(candidates, selectedCandidates)}
                  className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-1 text-sm`}
                  title="Export selected to CSV"
                >
                  <FileDown className="w-4 h-4" />
                  <span>CSV</span>
                </button>
                <button
                  onClick={handleDeleteSelected}
                  className={`${recruitmentTheme.components.button.secondary} text-red-600 hover:text-red-700 flex items-center space-x-1 text-sm`}
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete Selected</span>
                </button>
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="flex items-center space-x-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={areAllOnPageSelected}
                onChange={(e) => handleSelectAll(e.target.checked)}
                className="rounded"
              />
              <span>Select All on Page</span>
            </label>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cream-primary"></div>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {paginatedCandidates.map((candidate, index) => {
                  const candidateId = getCandidateId(candidate);
                  const isExpanded = expandedCandidates.has(candidateId);
                  const overallIndex = (currentPage - 1) * 10 + index + 1;
                  
                  return (
                    <div key={candidateId} className="transition-all duration-200">
                      {/* Collapsed View - Single Line Card */}
                      <div
                        className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer"
                        onClick={() => toggleCandidateExpansion(candidateId)}
                      >
                        <div className="p-4">
                          <div className="flex items-center justify-between">
                            {/* Left Section - Basic Info */}
                            <div className="flex items-center space-x-4 flex-grow">
                              <input
                                type="checkbox"
                                checked={selectedCandidates.includes(candidateId)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleSelectCandidate(candidateId, e.target.checked);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="rounded"
                              />
                              
                              <span className="font-bold text-[#8b6914] bg-[#f0e4cc] px-2 py-1 rounded text-sm min-w-[2rem] text-center">
                                {overallIndex}.
                              </span>
                              
                              <div className="min-w-0 flex-shrink">
                                <div className="font-medium text-[#8b6914] truncate">{candidate.name}</div>
                                <div className="text-sm text-gray-500 truncate">
                                  <span className="text-green-600">{candidate.phone || 'No phone'}</span>
                                  <span className="mx-2 text-gray-400">•</span>
                                  <span className="text-blue-600">{candidate.email || 'No email'}</span>
                                </div>
                              </div>
                              
                              <div className="hidden md:flex items-center space-x-4 text-sm flex-wrap">
                                <div className="min-w-0">
                                  <span className="text-gray-600">Emergency:</span>
                                  <span className="ml-1 text-gray-900 font-medium">{(candidate as any).emergencyContact || 'N/A'}</span>
                                </div>
                                
                                <StatusBadge
                                  currentStatus={candidate.stage}
                                  options={candidateStatusOptions}
                                  onStatusChange={(newStatus) => {
                                    if (candidateId) {
                                      updateCandidateStage(candidateId, newStatus);
                                    }
                                  }}
                                  size="sm"
                                />
                                
                                <div className="min-w-0">
                                  <span className="text-gray-600">Position:</span>
                                  <span className="ml-1 text-gray-900">{candidate.jobTitle || 'N/A'}</span>
                                </div>
                                
                                <div className="min-w-0">
                                  <span className="text-gray-600">Departure:</span>
                                  <span className="ml-1 text-gray-900">
                                    {((candidate as any).departureDate && (candidate as any).departureTime) 
                                      ? `${formatDate((candidate as any).departureDate)}`
                                      : 'Not set'}
                                  </span>
                                </div>
                                
                                <div className="min-w-0">
                                  <span className="text-gray-600">Company:</span>
                                  <span className="ml-1 text-gray-900">{(candidate as any).workCompany || 'N/A'}</span>
                                </div>
                                
                                <div className="min-w-0">
                                  <span className="text-gray-600">Salary:</span>
                                  <span className="ml-1 text-gray-900 font-medium">{formatCurrency(candidate.salary)}</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Right Section - Actions & Expand */}
                            <div className="flex items-center space-x-2 ml-4">
                              <div className="flex items-center space-x-1" onClick={(e) => e.stopPropagation()}>
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/cv-builder?candidateId=${candidateId}`, { 
                                      state: { candidate } 
                                    });
                                  }}
                                  className="text-blue-600 hover:text-blue-800 p-1"
                                  title="Create/Edit CV"
                                >
                                  <User className="w-4 h-4" />
                                </button>
                                
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedCandidate(candidate);
                                    setFormData({
                                      name: candidate.name,
                                      email: candidate.email || '',
                                      phone: candidate.phone || '',
                                      nationality: candidate.nationality || '',
                                      passportNumber: candidate.passportNumber || '',
                                      passportIssueDate: candidate.passportIssueDate || '',
                                      passportExpiryDate: candidate.passportExpiryDate || '',
                                      jobTitle: candidate.jobTitle || '',
                                      salary: candidate.salary || '',
                                      educationLevel: candidate.educationLevel || '',
                                      englishLevel: candidate.englishLevel || '',
                                      arabicLevel: candidate.arabicLevel || '',
                                      workPosition: (candidate as any).workPosition || '',
                                      workYears: (candidate as any).workYears || '',
                                      city: candidate.city || '',
                                      country: candidate.country || '',
                                      countryApplyingTo: candidate.countryApplyingTo || '',
                                      stage: candidate.stage || 'New Candidate',
                                      spouse: (candidate as any).spouse || '',
                                      numberOfKids: (candidate as any).numberOfKids || '',
                                      father: (candidate as any).father || '',
                                      mother: (candidate as any).mother || '',
                                      emergencyContact: (candidate as any).emergencyContact || '',
                                      workCompany: (candidate as any).workCompany || '',
                                      workCity: (candidate as any).workCity || '',
                                      notes: candidate.notes || ''
                                    });
                                    setShowEditModal(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-800 p-1"
                                  title="Edit Candidate"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteCandidate(candidateId);
                                  }}
                                  className="text-red-600 hover:text-red-800 p-1"
                                  title="Delete Candidate"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                              
                              {isExpanded ? (
                                <ChevronUp className="w-5 h-5 text-gray-400" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Expanded View - Detailed Grid */}
                      {isExpanded && (
                        <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-6 animate-fadeIn">
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Contact Details */}
                            <div className="space-y-3">
                              <h4 className="font-semibold text-[#8b6914] mb-3">Contact Information</h4>
                              <div>
                                <span className="text-sm text-gray-600">Email:</span>
                                <a 
                                  href={`mailto:${candidate.email}`}
                                  className="ml-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {candidate.email || 'N/A'}
                                </a>
                              </div>
                              <div>
                                <span className="text-sm text-gray-600">Phone:</span>
                                <a 
                                  href={`tel:${candidate.phone}`}
                                  className="ml-2 text-sm text-green-600 hover:text-green-800 hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {candidate.phone || 'N/A'}
                                </a>
                              </div>
                              <div>
                                <span className="text-sm text-gray-600">Emergency Contact:</span>
                                <span className="ml-2 text-sm text-gray-900">{(candidate as any).emergencyContact || 'N/A'}</span>
                              </div>
                            </div>
                            
                            {/* Work Details */}
                            <div className="space-y-3">
                              <h4 className="font-semibold text-[#8b6914] mb-3">Work Details</h4>
                              <div>
                                <span className="text-sm text-gray-600">Position:</span>
                                <span className="ml-2 text-sm text-gray-900">{candidate.jobTitle || 'N/A'}</span>
                              </div>
                              <div>
                                <span className="text-sm text-gray-600">Company:</span>
                                <span className="ml-2 text-sm text-gray-900">{(candidate as any).workCompany || 'N/A'}</span>
                              </div>
                              <div>
                                <span className="text-sm text-gray-600">City:</span>
                                <span className="ml-2 text-sm text-gray-900">{(candidate as any).workCity || 'N/A'}</span>
                              </div>
                              <div>
                                <span className="text-sm text-gray-600">Country:</span>
                                <span className="ml-2 text-sm text-gray-900">{candidate.countryApplyingTo || 'N/A'}</span>
                              </div>
                              <div>
                                <span className="text-sm text-gray-600">Salary:</span>
                                <span className="ml-2 text-sm text-gray-900">{formatCurrency(candidate.salary)}</span>
                              </div>
                            </div>
                            
                            {/* Additional Info */}
                            <div className="space-y-3">
                              <h4 className="font-semibold text-[#8b6914] mb-3">Additional Information</h4>
                              <div>
                                <span className="text-sm text-gray-600">Stage:</span>
                                <div className="mt-1">
                                  <StatusBadge
                                    currentStatus={candidate.stage}
                                    options={candidateStatusOptions}
                                    onStatusChange={(newStatus) => {
                                      if (candidateId) {
                                        updateCandidateStage(candidateId, newStatus);
                                      }
                                    }}
                                    size="sm"
                                  />
                                </div>
                              </div>
                              <div>
                                <span className="text-sm text-gray-600">Departure:</span>
                                <span className="ml-2 text-sm text-gray-900">
                                  {((candidate as any).departureDate && (candidate as any).departureTime) 
                                    ? `${formatDate((candidate as any).departureDate)} at ${(candidate as any).departureTime}`
                                    : 'Not set'}
                                </span>
                              </div>
                              <div>
                                <span className="text-sm text-gray-600">Added:</span>
                                <span className="ml-2 text-sm text-gray-900">
                                  {candidate.createdAt ? formatDate(candidate.createdAt) : 'N/A'}
                                </span>
                              </div>
                              {candidate.notes && (
                                <div>
                                  <span className="text-sm text-gray-600">Notes:</span>
                                  <p className="mt-1 text-sm text-gray-900">{candidate.notes}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {candidates.length === 0 && (
                <div className="text-center py-12">
                  <Users className="mx-auto h-12 w-12 text-[#8b6914] opacity-50" />
                  <h3 className="mt-2 text-sm font-medium text-[#8b6914]">No candidates found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {searchTerm || stageFilter !== 'all' 
                      ? 'Try adjusting your search or filter criteria.'
                      : 'Get started by adding your first candidate.'
                    }
                  </p>
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      className={recruitmentTheme.components.button.secondary}
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <button
                      className={recruitmentTheme.components.button.secondary}
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </div>

      {/* Add Candidate Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-[#8b6914]">Add New Candidate</h2>
                <p className="text-sm text-gray-600 mt-1">Fill in the information for the new candidate. Full Name is compulsory.</p>
              </div>
              <button 
                onClick={() => { setShowAddModal(false); resetForm(); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* REQUIRED: Full Name - First and most important */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="e.g., John Smith"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">This is the only required field. All others are optional.</p>
              </div>
              
              {/* Stage/Status */}
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Current Stage</label>
                <select
                  value={formData.stage}
                  onChange={(e) => setFormData({...formData, stage: e.target.value})}
                  className={recruitmentTheme.components.select}
                >
                  {stages.map(stage => (
                    <option key={stage} value={stage}>{stage}</option>
                  ))}
                </select>
              </div>
              
              {/* Nationality */}
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Nationality</label>
                <input
                  type="text"
                  value={formData.nationality}
                  onChange={(e) => setFormData({...formData, nationality: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="e.g., Kenyan, Ugandan"
                />
              </div>
              
              {/* Passport Number & Details */}
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Passport No.</label>
                <input
                  type="text"
                  value={formData.passportNumber}
                  onChange={(e) => setFormData({...formData, passportNumber: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="e.g., A12345678"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Passport Issue Date</label>
                <input
                  type="date"
                  value={formData.dateIssued}
                  onChange={(e) => setFormData({...formData, dateIssued: e.target.value})}
                  className={recruitmentTheme.components.input}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Passport Expiry Date</label>
                <input
                  type="date"
                  value={formData.dateExpiry}
                  onChange={(e) => setFormData({...formData, dateExpiry: e.target.value})}
                  className={recruitmentTheme.components.input}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Passport Place Issued</label>
                <input
                  type="text"
                  value={formData.placeIssued}
                  onChange={(e) => setFormData({...formData, placeIssued: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="e.g., Nairobi"
                />
              </div>
              
              {/* Personal Details */}
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Date of Birth</label>
                <input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => setFormData({...formData, dateOfBirth: e.target.value})}
                  className={recruitmentTheme.components.input}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Place of Birth</label>
                <input
                  type="text"
                  value={formData.placeOfBirth}
                  onChange={(e) => setFormData({...formData, placeOfBirth: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="e.g., Mombasa"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Age</label>
                <input
                  type="number"
                  value={formData.age}
                  onChange={(e) => setFormData({...formData, age: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="e.g., 28"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Religion</label>
                <select
                  value={formData.religion}
                  onChange={(e) => setFormData({...formData, religion: e.target.value})}
                  className={recruitmentTheme.components.select}
                >
                  <option value="">Select religion</option>
                  <option value="Muslim">Muslim</option>
                  <option value="Christian">Christian</option>
                  <option value="Hindu">Hindu</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              {/* Physical Details */}
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Height</label>
                <input
                  type="text"
                  value={formData.height}
                  onChange={(e) => setFormData({...formData, height: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="e.g., 5'6'' or 168cm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Weight</label>
                <input
                  type="text"
                  value={formData.weight}
                  onChange={(e) => setFormData({...formData, weight: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="e.g., 65kg"
                />
              </div>
              
              {/* Family Details */}
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Civil Status</label>
                <select
                  value={formData.civilStatus}
                  onChange={(e) => setFormData({...formData, civilStatus: e.target.value})}
                  className={recruitmentTheme.components.select}
                >
                  <option value="">Select status</option>
                  <option value="Single">Single</option>
                  <option value="Married">Married</option>
                  <option value="Divorced">Divorced</option>
                  <option value="Widowed">Widowed</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Spouse Name</label>
                <input
                  type="text"
                  value={formData.spouse}
                  onChange={(e) => setFormData({...formData, spouse: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="Leave blank if single"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Number of Children</label>
                <input
                  type="number"
                  value={formData.numberOfKids}
                  onChange={(e) => setFormData({...formData, numberOfKids: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Father's Name</label>
                <input
                  type="text"
                  value={formData.father}
                  onChange={(e) => setFormData({...formData, father: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="Father's full name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Mother's Name</label>
                <input
                  type="text"
                  value={formData.mother}
                  onChange={(e) => setFormData({...formData, mother: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="Mother's full name"
                />
              </div>
              
              {/* Location Details */}
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Current City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="e.g., Nairobi"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Current Country</label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({...formData, country: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="e.g., Kenya"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Country Applying To</label>
                <select
                  value={formData.countryApplyingTo}
                  onChange={(e) => setFormData({...formData, countryApplyingTo: e.target.value})}
                  className={recruitmentTheme.components.select}
                >
                  <option value="">Select destination country</option>
                  {countries.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
              
              {/* Education & Languages */}
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Education Level</label>
                <select
                  value={formData.educationLevel}
                  onChange={(e) => setFormData({...formData, educationLevel: e.target.value})}
                  className={recruitmentTheme.components.select}
                >
                  <option value="">Select education level</option>
                  <option value="Primary">Primary School</option>
                  <option value="Secondary">Secondary School</option>
                  <option value="College">College</option>
                  <option value="University">University</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">English Level</label>
                <select
                  value={formData.englishLevel}
                  onChange={(e) => setFormData({...formData, englishLevel: e.target.value})}
                  className={recruitmentTheme.components.select}
                >
                  <option value="">Select level</option>
                  <option value="None">None</option>
                  <option value="Basic">Basic</option>
                  <option value="Good">Good</option>
                  <option value="Excellent">Excellent</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Arabic Level</label>
                <select
                  value={formData.arabicLevel}
                  onChange={(e) => setFormData({...formData, arabicLevel: e.target.value})}
                  className={recruitmentTheme.components.select}
                >
                  <option value="">Select level</option>
                  <option value="None">None</option>
                  <option value="Basic">Basic</option>
                  <option value="Good">Good</option>
                  <option value="Excellent">Excellent</option>
                </select>
              </div>
              
              {/* Work Experience */}
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Desired Job Title</label>
                <input
                  type="text"
                  value={formData.jobTitle}
                  onChange={(e) => setFormData({...formData, jobTitle: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="e.g., Domestic Helper, Driver, Nanny"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Previous Work Position</label>
                <input
                  type="text"
                  value={formData.workPosition}
                  onChange={(e) => setFormData({...formData, workPosition: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="Previous job title"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Years of Experience</label>
                <input
                  type="number"
                  value={formData.workYears}
                  onChange={(e) => setFormData({...formData, workYears: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="e.g., 5"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Expected Salary (KES)</label>
                <input
                  type="number"
                  value={formData.salary}
                  onChange={(e) => setFormData({...formData, salary: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="e.g., 150000"
                />
              </div>
              
              
              {/* Contact Details */}
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="e.g., +254712345678"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Email Address</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className={recruitmentTheme.components.input}
                  placeholder="e.g., john.smith@email.com"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Additional Notes / Remarks</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className={`${recruitmentTheme.components.textarea} h-20`}
                  placeholder="Any relevant notes about the candidate's skills, preferences, medical conditions, etc."
                />
              </div>
              
              {/* Create CV Button - Placed at bottom after all other fields */}
              <div className="md:col-span-2">
                <button 
                  onClick={handleCreateCV}
                  className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-2 w-full justify-center`}
                  type="button"
                >
                  <User className="w-4 h-4" />
                  <span>Create CV</span>
                </button>
                <p className="text-xs text-gray-500 mt-1 text-center">
                  Fill in candidate details above, then click to create a CV in the CV Builder.
                  The details will be automatically transferred to the CV Builder.
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button 
                onClick={() => { setShowAddModal(false); resetForm(); }}
                className={recruitmentTheme.components.button.secondary}
              >
                Cancel
              </button>
              <button 
                onClick={handleAddCandidate}
                className={`${recruitmentTheme.components.button.primary} flex items-center space-x-2`}
                disabled={!formData.name}
              >
                <Save className="w-4 h-4" />
                <span>Save Candidate</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Candidate Modal */}
      {showEditModal && selectedCandidate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#8b6914]">Edit Candidate</h2>
              <button 
                onClick={() => { setShowEditModal(false); setSelectedCandidate(null); resetForm(); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className={recruitmentTheme.components.input}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className={recruitmentTheme.components.input}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Phone *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className={recruitmentTheme.components.input}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Job Title</label>
                <input
                  type="text"
                  value={formData.jobTitle}
                  onChange={(e) => setFormData({...formData, jobTitle: e.target.value})}
                  className={recruitmentTheme.components.input}
                />
              </div>
              
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Salary (KES)</label>
                <input
                  type="number"
                  value={formData.salary}
                  onChange={(e) => setFormData({...formData, salary: e.target.value})}
                  className={recruitmentTheme.components.input}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Stage</label>
                <select
                  value={formData.stage}
                  onChange={(e) => setFormData({...formData, stage: e.target.value})}
                  className={recruitmentTheme.components.select}
                >
                  {stages.map(stage => (
                    <option key={stage} value={stage}>{stage}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Country Applying To</label>
                <input
                  type="text"
                  value={formData.countryApplyingTo}
                  onChange={(e) => setFormData({...formData, countryApplyingTo: e.target.value})}
                  className={recruitmentTheme.components.input}
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#8b6914] mb-1">Passport Number</label>
                <input
                  type="text"
                  value={formData.passportNumber}
                  onChange={(e) => setFormData({...formData, passportNumber: e.target.value})}
                  className={recruitmentTheme.components.input}
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button 
                onClick={() => { setShowEditModal(false); setSelectedCandidate(null); resetForm(); }}
                className={recruitmentTheme.components.button.secondary}
              >
                Cancel
              </button>
              <button 
                onClick={handleEditCandidate}
                className={`${recruitmentTheme.components.button.primary} flex items-center space-x-2`}
                disabled={!formData.name || !formData.email || !formData.phone}
              >
                <Save className="w-4 h-4" />
                <span>Update Candidate</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auto-Deletion Settings Modal */}
      {showAutoDeletionSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#8b6914]">Auto-Deletion Settings</h2>
              <button 
                onClick={() => setShowAutoDeletionSettings(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={autoDeletionEnabled}
                  onChange={handleAutoDeletionToggle}
                  className="rounded"
                />
                <label className="text-sm font-medium text-[#8b6914]">
                  Enable auto-deletion for completed candidates
                </label>
              </div>
              
              {autoDeletionEnabled && (
                <div>
                  <label className="block text-sm font-medium text-[#8b6914] mb-1">
                    Delete after (days):
                  </label>
                  <select
                    value={autoDeletionDays}
                    onChange={(e) => setAutoDeletionDays(parseInt(e.target.value))}
                    className={recruitmentTheme.components.select}
                  >
                    <option value={7}>7 days</option>
                    <option value={14}>14 days (2 weeks)</option>
                    <option value={30}>30 days (1 month)</option>
                    <option value={60}>60 days (2 months)</option>
                    <option value={90}>90 days (3 months)</option>
                  </select>
                </div>
              )}
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Auto-Deletion Policy</p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Completed candidates will be automatically deleted after the specified number of days. 
                      This helps maintain a clean database and removes old records.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button 
                onClick={() => setShowAutoDeletionSettings(false)}
                className={recruitmentTheme.components.button.secondary}
              >
                Cancel
              </button>
              {autoDeletionEnabled && (
                <button 
                  onClick={executeAutoDeletion}
                  className={`${recruitmentTheme.components.button.primary} flex items-center space-x-2`}
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Execute Now</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidatesPage;
